import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { BookingDTO, CondimentDTO, TableDTO } from '@six-pockets/shared';
import { condimentSalesApi, condimentsApi, bookingsApi, tablesApi, type BookingAction } from '@/api';
import { isBillable } from '@/utils/booking';
import { msUntilMidnight, todayKey } from '@/utils/date';

/**
 * Tables page state, split into four contexts by how often each part changes, so a component re-renders only
 * for what it reads (this runs on a 4 GB laptop):
 * - actions (useTableActions): stable functions; never cause a re-render.
 * - data (useTablesData): tables, the condiment menu, loading and error; changes on every booking action.
 * - selection (useSelection): ticked bookings; changes on every checkbox.
 * - open-tab count (useOpenSalesCount): the Condiments sidebar badge.
 * Updated tables are reconciled with the old ones, so unchanged tables and bookings keep their object identity
 * and memoised cards and rows skip re-rendering.
 */

type Selection = Record<string, string[]>;

/** A booking ticked for billing, with its table's name (a bill can cover several tables). */
export type SelectedBooking = BookingDTO & { tableName: string };

interface TablesData {
  tables: TableDTO[];
  condiments: CondimentDTO[];
  loading: boolean;
  /** Last failed request, shown inline until dismissed. */
  error: string | null;
}

interface SelectionState {
  /** Ended bookings ticked for billing, per table. */
  selection: Selection;
  /** Every ticked booking across tables, earliest first. */
  selectedBookings: SelectedBooking[];
}

interface TableActions {
  dismissError: () => void;
  refresh: () => Promise<void>;
  /** Swaps updated tables into state (every booking and bill action returns them). */
  replaceTables: (tables: TableDTO[]) => void;
  startTable: (tableId: string) => Promise<void>;
  bookingAction: (bookingId: string, action: BookingAction) => Promise<void>;
  renameBooking: (bookingId: string, name: string) => Promise<void>;
  /** Gives the booking an existing customer (it takes their name). */
  setBookingCustomer: (bookingId: string, customerId: string) => Promise<void>;
  /** The second name (doubles: the other team): typed ('' removes it), or an existing customer. */
  setSecondName: (bookingId: string, name: { customerName2: string } | { customerId2: string }) => Promise<void>;
  refreshOpenSales: () => Promise<void>;
  toggleSelected: (tableId: string, bookingId: string) => void;
  clearSelection: (tableId: string) => void;
  clearAllSelections: () => void;
}

const DataContext = createContext<TablesData | null>(null);
const SelectionContext = createContext<SelectionState | null>(null);
const ActionsContext = createContext<TableActions | null>(null);
/** Condiment tabs opened today and still open, for the Condiments sidebar badge. */
const OpenSalesContext = createContext(0);

/** Shared by every table with no ticked bookings, so its card's props stay equal. */
export const NO_SELECTION: readonly string[] = Object.freeze([]);

/** Drops selected ids that are no longer billable (e.g. billed elsewhere, or off the cards after midnight). */
function pruneSelection(selection: Selection, table: TableDTO): Selection {
  const current = selection[table.id];
  if (!current?.length) return selection;
  const billable = new Set(table.bookings.filter(isBillable).map((s) => s.id));
  const next = current.filter((id) => billable.has(id));
  return next.length === current.length ? selection : { ...selection, [table.id]: next };
}

/** Booking fields are all primitives, so a field-by-field check tells whether anything changed. */
function sameBooking(a: BookingDTO, b: BookingDTO): boolean {
  return (Object.keys(b) as (keyof BookingDTO)[]).every((k) => a[k] === b[k]);
}

/** The new table, reusing the old table and booking objects wherever nothing changed. */
function reconcileTable(prev: TableDTO | undefined, next: TableDTO): TableDTO {
  if (!prev) return next;
  const prevBookings = new Map(prev.bookings.map((b) => [b.id, b]));
  let changed = prev.bookings.length !== next.bookings.length;
  const bookings = next.bookings.map((b, i) => {
    const old = prevBookings.get(b.id);
    if (old && sameBooking(old, b)) {
      if (prev.bookings[i] !== old) changed = true;
      return old;
    }
    changed = true;
    return b;
  });
  const sameTable =
    prev.name === next.name && prev.kind === next.kind && prev.ratePerHour === next.ratePerHour && prev.status === next.status;
  return !changed && sameTable ? prev : { ...next, bookings };
}

function reconcileAll(prev: TableDTO[], next: TableDTO[]): TableDTO[] {
  const byId = new Map(prev.map((t) => [t.id, t]));
  const merged = next.map((t) => reconcileTable(byId.get(t.id), t));
  return merged.length === prev.length && merged.every((t, i) => t === prev[i]) ? prev : merged;
}

export function TablesProvider({ children }: { children: ReactNode }) {
  const [tables, setTables] = useState<TableDTO[]>([]);
  const [condiments, setCondiments] = useState<CondimentDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>({});
  const [openSalesCount, setOpenSalesCount] = useState(0);

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    try {
      setError(null);
      return await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      return undefined;
    }
  }, []);

  const replaceTables = useCallback((updated: TableDTO[]) => {
    setTables((prev) => {
      const byId = new Map(updated.map((t) => [t.id, t]));
      const next = prev.map((t) => (byId.has(t.id) ? reconcileTable(t, byId.get(t.id)!) : t));
      return next.every((t, i) => t === prev[i]) ? prev : next;
    });
    setSelection((prev) => updated.reduce(pruneSelection, prev));
  }, []);

  /** Replaces the whole list (initial load, resync), keeping unchanged tables' identity. */
  const setAllTables = useCallback((next: TableDTO[]) => {
    setTables((prev) => reconcileAll(prev, next));
    setSelection((prev) => next.reduce(pruneSelection, prev));
  }, []);

  const refreshOpenSales = useCallback(async () => {
    try {
      setOpenSalesCount((await condimentSalesApi.summary({ date: todayKey() })).openCount);
    } catch {
      // The badge is informational; ignore failures.
    }
  }, []);

  const refresh = useCallback(async () => {
    await run(async () => {
      const [t, c] = await Promise.all([tablesApi.list(), condimentsApi.list()]);
      setAllTables(t);
      setCondiments(c);
    });
    setLoading(false);
    void refreshOpenSales();
  }, [run, setAllTables, refreshOpenSales]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The cards show today's bookings and the badge counts today's tabs, so reload both when the day changes
  // (yesterday's ended, unbilled bookings leave the cards; live ones stay).
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      timer = setTimeout(() => {
        void refreshOpenSales();
        tablesApi.list().then(setAllTables, () => undefined);
        schedule();
      }, msUntilMidnight() + 1000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, [refreshOpenSales, setAllTables]);

  const withTable = useCallback(
    async (fn: () => Promise<TableDTO>) => {
      const table = await run(fn);
      if (table) {
        replaceTables([table]);
        return;
      }
      // The action failed (often because another counter changed the table): resync quietly.
      tablesApi.list().then(setAllTables, () => undefined);
    },
    [run, replaceTables, setAllTables],
  );

  const actions = useMemo<TableActions>(
    () => ({
      dismissError: () => setError(null),
      refresh,
      replaceTables,
      startTable: (tableId) => withTable(() => tablesApi.start(tableId)),
      bookingAction: (bookingId, action) => withTable(() => bookingsApi.act(bookingId, action)),
      renameBooking: (bookingId, name) => withTable(() => bookingsApi.rename(bookingId, name)),
      setBookingCustomer: (bookingId, customerId) => withTable(() => bookingsApi.setCustomer(bookingId, customerId)),
      setSecondName: (bookingId, name) => withTable(() => bookingsApi.update(bookingId, name)),
      refreshOpenSales,
      toggleSelected: (tableId, bookingId) =>
        setSelection((prev) => {
          const current = prev[tableId] ?? [];
          const next = current.includes(bookingId) ? current.filter((id) => id !== bookingId) : [...current, bookingId];
          return { ...prev, [tableId]: next };
        }),
      clearSelection: (tableId) => setSelection((prev) => (prev[tableId]?.length ? { ...prev, [tableId]: [] } : prev)),
      clearAllSelections: () => setSelection((prev) => (Object.values(prev).some((ids) => ids.length) ? {} : prev)),
    }),
    // All stable (useCallback with stable deps), so this object is created once.
    [refresh, replaceTables, withTable, refreshOpenSales],
  );

  const data = useMemo<TablesData>(() => ({ tables, condiments, loading, error }), [tables, condiments, loading, error]);

  const selectionState = useMemo<SelectionState>(() => {
    const selectedBookings = tables
      .flatMap((t) => t.bookings.filter((b) => selection[t.id]?.includes(b.id) && isBillable(b)).map((b) => ({ ...b, tableName: t.name })))
      .sort((a, b) => a.startedAt.localeCompare(b.startedAt));
    return { selection, selectedBookings };
  }, [tables, selection]);

  return (
    <ActionsContext.Provider value={actions}>
      <DataContext.Provider value={data}>
        <SelectionContext.Provider value={selectionState}>
          <OpenSalesContext.Provider value={openSalesCount}>{children}</OpenSalesContext.Provider>
        </SelectionContext.Provider>
      </DataContext.Provider>
    </ActionsContext.Provider>
  );
}

function required<T>(value: T | null, hook: string): T {
  if (value === null) throw new Error(`${hook} must be used inside <TablesProvider>`);
  return value;
}

/** Stable action functions. Reading only these never re-renders a component. */
export const useTableActions = () => required(useContext(ActionsContext), 'useTableActions');
/** Tables, the condiment menu, loading and error. */
export const useTablesData = () => required(useContext(DataContext), 'useTablesData');
/** Ticked bookings (per table, and the combined list for the bill). */
export const useSelection = () => required(useContext(SelectionContext), 'useSelection');
/** Today's open condiment tabs, for the sidebar badge. */
export const useOpenSalesCount = () => useContext(OpenSalesContext);
